// The Talk button and the call strip (TOM-731): a live voice call with Neo
// from the chat box. Talk sits beside the dictation mic, which stays; during a
// call a strip above the box says what the call is doing (connecting,
// listening, thinking, speaking), with Mute and End. Answers land in the chat
// as they would typed, and the box stays open for typing in the same
// conversation.

import type { NeoVoice, NeoVoicePhase } from '../../lib/useNeoVoice';

const PHASE_LINE: Record<Exclude<NeoVoicePhase, 'idle'>, string> = {
  connecting: 'Connecting…',
  listening: 'Listening',
  thinking: 'Neo is thinking',
  speaking: 'Neo is speaking',
};

/** Talk: starts a call. Drawn only where voice can be offered and no call is on. */
export function NeoTalkButton({
  onTalk,
  className,
  testId = 'neo-talk',
}: {
  onTalk: () => void;
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={`chat-composer__icon chat-composer__talk${className === undefined ? '' : ` ${className}`}`}
      aria-label="Talk to Neo"
      title="Talk to Neo"
      onClick={onTalk}
      data-testid={testId}
    >
      <TalkIcon />
    </button>
  );
}

/** The call, above the chat box: its state, Mute and End; or why it ended, with Try again when it never connected. */
export function NeoVoiceStrip({ voice }: { voice: NeoVoice }) {
  if (voice.phase === 'idle') {
    if (voice.notice === null) return null;
    return (
      <p className="neo-voice-note" role="status" data-testid="neo-voice-notice">
        <span>{voice.notice}</span>
        {voice.retry && voice.available ? (
          <button
            type="button"
            className="neo-voice-note__retry"
            onClick={voice.start}
            data-testid="neo-voice-retry"
          >
            Try again
          </button>
        ) : null}
        <button
          type="button"
          className="neo-voice-note__close"
          aria-label="Dismiss"
          onClick={voice.dismissNotice}
        >
          <CloseIcon />
        </button>
      </p>
    );
  }
  const line = voice.muted && voice.phase === 'listening' ? 'Muted' : PHASE_LINE[voice.phase];
  return (
    <>
      <div
        className="neo-voice-strip"
        data-phase={voice.phase}
        data-testid="neo-voice-strip"
        role="group"
        aria-label="Voice call with Neo"
      >
        <span className="neo-voice-strip__wave" aria-hidden="true">
          <i />
          <i />
          <i />
          <i />
        </span>
        <span className="neo-voice-strip__state" role="status" data-testid="neo-voice-state">
          {line}
        </span>
        {voice.notice !== null ? (
          <span className="neo-voice-strip__notice">{voice.notice}</span>
        ) : null}
        <button
          type="button"
          className="neo-voice-strip__btn"
          aria-pressed={voice.muted}
          aria-label={voice.muted ? 'Unmute' : 'Mute'}
          title={voice.muted ? 'Unmute' : 'Mute'}
          disabled={voice.phase === 'connecting'}
          onClick={voice.toggleMute}
          data-testid="neo-voice-mute"
        >
          {voice.muted ? <MicOffIcon /> : <MicIcon />}
        </button>
        <button
          type="button"
          className="neo-voice-strip__end"
          onClick={voice.end}
          data-testid="neo-voice-end"
        >
          <PhoneOffIcon />
          <span>End</span>
        </button>
      </div>
      {voice.hint !== null ? (
        // The microphone sends nothing (TOM-731): said under the strip while the call runs.
        <p
          className="neo-voice-note neo-voice-note--hint"
          role="status"
          data-testid="neo-voice-hint"
        >
          <span>{voice.hint}</span>
        </p>
      ) : null}
    </>
  );
}

// Lucide glyphs, drawn with the stroke the stylesheet gives them.
function TalkIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 10v3" />
      <path d="M6 6v11" />
      <path d="M10 3v18" />
      <path d="M14 8v7" />
      <path d="M18 5v13" />
      <path d="M22 10v3" />
    </svg>
  );
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
      <path d="M12 18v4" />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 18v4" />
      <path d="m2 2 20 20" />
      <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33" />
      <path d="M9 9v2a3 3 0 0 0 5.12 2.12" />
      <path d="M16.95 16.95A7 7 0 0 1 5 12v-2" />
      <path d="M19 10v2a7 7 0 0 1-.11 1.23" />
    </svg>
  );
}

function PhoneOffIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M10.1 13.9a14 14 0 0 0 3.732 2.668 1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2 18 18 0 0 1-12.728-5.272" />
      <path d="M22 2 2 22" />
      <path d="M4.76 13.582A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 .244.473" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}
